import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { agoIso, fmtTime } from "../format";
import { navigate } from "../route";
import type { DeliveryPage, DeliveryRow, DeliveryStats, MessageKind, TaskEnv } from "../types";
import { secs, Tile } from "./latencyParts";

/**
 * Message delivery: Sent → Delivered, one row per outbound text.
 *
 * Answers "once atula sent a text, how long until Sendblue said the phone got
 * it?" Built after 2026-10-16, when after-call texts on dev sat at SENT until
 * the recipient texted (the receipt landed 1s after her message) and prod
 * receipts lagged ~18h behind a user who was replying live.
 *
 * "Sent" is Sendblue accepting our API call; "Delivered" is the first DELIVERED
 * receipt. Both are stamped by our server's clock. A receipt is evidence, not
 * proof: a late receipt does not always mean a late message — the user may have
 * seen it long before Sendblue reported it.
 *
 * Shares env and window with the reply section above; the kind filter is local.
 * Read-only. Rows carry the hashed user id and masked recipient only.
 */

const KIND_LABEL: Record<MessageKind, string> = {
  reply: "Reply",
  meeting: "Meeting link",
  plan: "Plan",
  recap: "Session recap",
  next_session: "Next session",
  nudge: "Nudge",
  onboarding: "Onboarding",
  resource: "Resource",
  other: "Other / untagged",
};

function StatusBadge({ r }: { r: DeliveryRow }) {
  if (r.failed) return <span className="badge bad">{r.latest_status || "failed"}</span>;
  if (r.stuck) return <span className="badge warn" title="No delivery receipt yet">no receipt</span>;
  if (!r.delivered_at) return <span className="badge">{r.latest_status || "sent"}</span>;
  return <span className="badge ok">delivered</span>;
}

function KindTable({
  byKind,
  threshold,
}: {
  byKind: Partial<Record<MessageKind, DeliveryStats>>;
  threshold: number;
}) {
  const kinds = (Object.keys(KIND_LABEL) as MessageKind[]).filter((k) => byKind[k]);
  if (!kinds.length) return null;
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Kind</th>
            <th className="right">Messages</th>
            <th className="right">p50</th>
            <th className="right">p90</th>
            <th className="right">Slowest</th>
            <th className="right">Slow (&gt; {threshold}s)</th>
            <th className="right">No receipt</th>
            <th className="right">On user text</th>
          </tr>
        </thead>
        <tbody>
          {kinds.map((k) => {
            const s = byKind[k]!;
            return (
              <tr key={k} className={s.slow_count || s.stuck_count ? "row-warn" : undefined}>
                <td>{KIND_LABEL[k]}</td>
                <td className="right">{s.count}</td>
                <td className="right">{secs(s.p50)}</td>
                <td className="right">{secs(s.p90)}</td>
                <td className="right">{secs(s.max)}</td>
                <td className="right">{s.slow_count}</td>
                <td className="right">{s.stuck_count}</td>
                <td className="right">{s.held_count}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function DeliveryLatency({ env, hours }: { env: TaskEnv; hours: number }) {
  const [kind, setKind] = useState<MessageKind | "">("");
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [page, setPage] = useState<DeliveryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPage(await api.deliveries(env, { hours, kind }));
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [env, hours, kind]);

  useEffect(() => {
    void load();
  }, [load]);

  const s = page?.summary;
  const threshold = s?.slow_threshold_s ?? 60;
  const rows = useMemo(() => page?.items ?? [], [page]);
  const isProblem = (r: DeliveryRow) =>
    r.failed || r.stuck || (r.sent_to_delivered_s ?? 0) > threshold;
  const visible = useMemo(
    () => (problemsOnly ? rows.filter(isProblem) : rows),
    [rows, problemsOnly, threshold], // isProblem depends only on threshold
  );

  return (
    <>
      <header className="section-head">
        <h2>Message delivery</h2>
        <button className="ghost" onClick={() => void load()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </header>

      <p className="muted small">
        Sent → Delivered (receipt): from Sendblue accepting a text to its first
        DELIVERED receipt. A late receipt is not always a late message — the user
        may have seen it before Sendblue reported it. "On user text" marks a slow
        receipt that arrived within {s?.held_window_s ?? 15}s of the user texting
        us, which suggests the message was held until their device woke up.
      </p>

      <div className="tabs">
        <span className="filter-label">Kind</span>
        <button className={kind === "" ? "tab active" : "tab"} onClick={() => setKind("")}>
          All
        </button>
        {(page?.kinds ?? (Object.keys(KIND_LABEL) as MessageKind[])).map((k) => (
          <button key={k} className={kind === k ? "tab active" : "tab"} onClick={() => setKind(k)}>
            {KIND_LABEL[k]}
          </button>
        ))}
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {s && (
        <div className="tiles">
          <Tile label="Messages" value={`${s.count}`} sub={`${s.delivered} with a receipt`} />
          <Tile label="Sent → Delivered p50" value={secs(s.p50)} />
          <Tile label="p90" value={secs(s.p90)} />
          <Tile label="Slowest" value={secs(s.max)} tone={(s.max ?? 0) > threshold ? "bad" : undefined} />
          <Tile
            label={`Slow (> ${threshold}s)`}
            value={`${s.slow_count}`}
            tone={s.slow_count > 0 ? "bad" : undefined}
            sub={s.delivered ? `${Math.round((s.slow_count / s.delivered) * 100)}% of receipts` : undefined}
          />
          <Tile
            label="No receipt"
            value={`${s.stuck_count}`}
            tone={s.stuck_count > 0 ? "bad" : undefined}
            sub={`still not delivered after ${Math.round(s.stuck_after_s / 60)} min`}
          />
          <Tile label="Delivered on user text" value={`${s.held_count}`} tone={s.held_count > 0 ? "bad" : undefined} />
          {s.failed_count > 0 && <Tile label="Failed" value={`${s.failed_count}`} tone="bad" />}
        </div>
      )}

      {s && kind === "" && <KindTable byKind={s.by_kind} threshold={threshold} />}

      {!loading && page && rows.length === 0 && (
        <p className="muted empty">
          No outbound texts{kind ? ` of kind "${KIND_LABEL[kind]}"` : ""} in this window
          {env === "development" ? " on dev" : ""}.
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="tabs">
            <button className={!problemsOnly ? "tab active" : "tab"} onClick={() => setProblemsOnly(false)}>
              All messages<span className="count">{rows.length}</span>
            </button>
            <button className={problemsOnly ? "tab active" : "tab"} onClick={() => setProblemsOnly(true)}>
              Slow or missing<span className="count">{rows.filter(isProblem).length}</span>
            </button>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Sent ▾</th>
                  <th>User</th>
                  <th>Kind</th>
                  <th className="right">Sent → Delivered</th>
                  <th>Status</th>
                  <th>Delivered at</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const slow = isProblem(r);
                  return (
                    <tr key={r.delivery_id} className={slow ? "row-warn" : undefined}>
                      <td className="nowrap" title={fmtTime(r.sent_at)}>
                        <span className="muted">{agoIso(r.sent_at)}</span>
                      </td>
                      <td>
                        <button
                          className="linklike small"
                          onClick={() => navigate({ tab: "users", q: r.user_id })}
                          title="Open this user"
                        >
                          <code>{r.user_id}</code>
                        </button>
                        <span className="muted small"> {r.recipient_masked}</span>
                      </td>
                      <td className="nowrap small" title={r.message_source || "untagged"}>
                        {KIND_LABEL[r.message_kind] ?? r.message_kind}
                        {r.message_source && r.message_source !== r.message_kind && (
                          <span className="muted"> · {r.message_source}</span>
                        )}
                      </td>
                      <td className="right nowrap">
                        <strong className={slow ? "slow" : undefined}>{secs(r.sent_to_delivered_s)}</strong>
                      </td>
                      <td className="nowrap small">
                        <StatusBadge r={r} />
                        {r.delivered_on_user_text && (
                          <span className="badge warn" title="Receipt arrived right after the user texted us">
                            {" "}on user text
                          </span>
                        )}
                      </td>
                      <td className="nowrap small muted">{r.delivered_at ? fmtTime(r.delivered_at) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {page && (
        <p className="muted small">
          {visible.length} shown · read from <code>message_deliveries</code>
          {page.complete
            ? ` (${page.scanned} sends in the window, all environments)`
            : ` — the newest ${page.scanned} sends only; older ones in the window were not read`}
          . Texts sent before the kind tag was added show as "Other / untagged".
        </p>
      )}
    </>
  );
}
